import { useMutation, useQuery } from '@tanstack/react-query';
import { httpService } from '../../../utils/apiService';
import queryString from 'query-string';

export const useFetchDashboardReport = ({ operatorId }, options = {}) => {
  return useQuery({
    queryKey: ['DASHBOARD_REPORT', operatorId],
    queryFn: async () => {
      const url = operatorId
        ? `report/dashboard?operatorId=${operatorId}`
        : 'report/dashboard';
      const response = await httpService.get(url);
      return response.data.data;
    },
    ...options,
    refetchOnWindowFocus: false,
  });
};

export const useFetchReportList = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['REPORT_LIST', params],
    queryFn: async () => {
      const filteredParams = Object.fromEntries(
        // eslint-disable-next-line no-unused-vars
        Object.entries(params).filter(([_, v]) => v != null && v !== '')
      );
      const queryParams = queryString.stringify(filteredParams);
      const response = await httpService.get(`report/list?${queryParams}`);
      return response.data.data;
    },
    enabled: Object.keys(params).length > 0,
    ...options,
  });
};

export const useDownloadPresignedUrl = (options = {}) => {
  return useMutation({
    mutationKey: ['DOWNLOAD_PRESIGNED_URL'],
    mutationFn: async ({ fileUrl }) => {
      const response = await httpService.post(
        `/get-file-presigned-url?filePath=${fileUrl}`
      );
      return response.data;
    },
    ...options,
  });
};

export const useMonthlyReportGeneration = (options = {}) => {
  return useMutation({
    mutationKey: ['MONTHLY_REPORT_GENERATION'],
    mutationFn: async (payload) => {
      const response = await httpService.post(
        'report/monthly-report-generation',
        payload
      );
      return response.data;
    },
    ...options,
  });
};

export const useRegenerateMonthlyReport = (options = {}) => {
  return useMutation({
    mutationKey: ['REGENERATE_MONTHLY_REPORT'],
    mutationFn: async ({ reportId }) => {
      const response = await httpService.post(
        `report/regenerate/${reportId}`,
        {}
      );
      return response.data;
    },
    ...options,
  });
};

export const useSubmitSapMonthlyReport = (options = {}) => {
  return useMutation({
    mutationKey: ['SUBMIT_SAP_MONTHLY_REPORT'],
    mutationFn: async ({ reportId, force = false }) => {
      const response = await httpService.post(`report/submit-sap/${reportId}`, {
        force,
      });
      return response.data;
    },
    ...options,
  });
};

export const useCustomerMonthlyPdf = (options = {}) => {
  return useMutation({
    mutationKey: ['CUSTOMER_MONTHLY_PDF'],
    mutationFn: async (payload) => {
      const response = await httpService.post(
        'report/customer-monthly-pdf',
        payload,
        {
          headers: {
            ...httpService.getHeaders(),
            'x-timezone': 'Europe/Istanbul',
          },
        }
      );
      return response.data;
    },
    ...options,
  });
};

export const useFetchChargePointCount = (
  { operatorId, fromDate, toDate },
  options = {}
) => {
  return useQuery({
    queryKey: ['CHARGE_POINT_COUNT', operatorId, fromDate, toDate],
    queryFn: async () => {
      const payload = {
        reportType: 'CHARGE_POINT_COUNT',
        ...(operatorId && { operatorId }),
        ...(fromDate && toDate && { fromDate, toDate }),
      };
      const response = await httpService.post('report/dynamic-report', payload);
      return response.data.data;
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useFetchChargingSession = (
  { operatorId, fromDate, toDate, isRFID, chargerId, locationId },
  options = {}
) => {
  return useQuery({
    queryKey: ['CHARGING_SESSION', operatorId, fromDate, toDate, isRFID, chargerId, locationId],
    queryFn: async () => {
      const payload = {
        reportType: 'CHARGING_SESSION',
        ...(operatorId && { operatorId }),
        ...(locationId && { locationId }),
        ...(fromDate && toDate && { fromDate, toDate }),
        ...(isRFID !== undefined && { isRFID }), // Handle all, RFID, or without RFID
        ...(chargerId && { chargerId }),
      };
      const response = await httpService.post('report/dynamic-report', payload);
      return response.data.data;
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useFetchEnergyConsumption = (
  { operatorId, fromDate, toDate, isRFID, chargerId, locationId },
  options = {}
) => {
  return useQuery({
    queryKey: ['ENERGY_CONSUMPTION', operatorId, fromDate, toDate, isRFID, chargerId, locationId],
    queryFn: async () => {
      const payload = {
        reportType: 'ENERGY_CONSUMPTION',
        ...(operatorId && { operatorId }),
        ...(locationId && { locationId }),
        ...(fromDate && toDate && { fromDate, toDate }),
        ...(chargerId && { chargerId }),
        ...(isRFID !== undefined && { isRFID }), // Handle all, RFID, or without RFID
      };
      const response = await httpService.post('report/dynamic-report', payload);
      return response.data.data;
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useFetchChargerOnlineTime = (
  { operatorId, fromDate, toDate, chargerId },
  options = {}
) => {
  return useQuery({
    queryKey: ['CHARGER_ONLINE_TIME', operatorId, fromDate, toDate, chargerId],
    queryFn: async () => {
      const payload = {
        reportType: 'CHARGER_ONLINE_TIME',
        ...(operatorId && { operatorId }),
        ...(chargerId && { chargerId }),
        ...(fromDate && toDate && { fromDate, toDate }),
      };
      const response = await httpService.post('report/dynamic-report', payload);
      return response.data.data;
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useFetchRevenueReport = (
  { operatorId, fromDate, toDate, isRFID, locationId, chargerId },
  options = {}
) => {
  return useQuery({
    queryKey: ['REVENUE', operatorId, fromDate, toDate, isRFID, locationId, chargerId],
    queryFn: async () => {
      const payload = {
        reportType: 'REVENUE',
        ...(operatorId && { operatorId }),
        ...(fromDate && toDate && { fromDate, toDate }),
        ...(isRFID !== undefined && { isRFID }), // Handle all, RFID, or without RFID
        ...(locationId && { locationId }),
        ...(chargerId && { chargerId }),
      };
      const response = await httpService.post('report/dynamic-report', payload);
      return response.data.data;
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useFetchUserCountReport = (
  { operatorId, fromDate, toDate },
  options = {}
) => {
  return useQuery({
    queryKey: ['USER_COUNT', operatorId, fromDate, toDate],
    queryFn: async () => {
      const payload = {
        reportType: 'USER_COUNT',
        ...(operatorId && { operatorId }),
        ...(fromDate && toDate && { fromDate, toDate }),
      };
      const response = await httpService.post('report/dynamic-report', payload);
      return response.data.data;
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useFetchUserAnalysisReport = (
  { customerId, fromDate, toDate, isRFID },
  options = {}
) => {
  return useQuery({
    queryKey: ['USER_ANALYSIS', customerId, fromDate, toDate, isRFID],
    queryFn: async () => {
      const payload = {
        reportType: 'USER_ANALYSIS',
        ...(customerId && { customerId }),
        ...(fromDate && toDate && { fromDate, toDate }),
        ...(isRFID !== undefined && { isRFID }), // Handle all, RFID, or without RFID
      };
      const response = await httpService.post('report/dynamic-report', payload);
      return response.data.data;
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useGenerateCustomReport = (options = {}) => {
  return useMutation({
    mutationKey: ['GENERATE_CUSTOM_REPORT'],
    mutationFn: async (payload) => {
      const response = await httpService.post('report/custom-report', payload);
      return response.data;
    },
    ...options,
  });
};

export const useFetchCustomReportList = (payload = {}, options = {}) => {
  return useQuery({
    queryKey: ['CUSTOM_REPORT_LIST', payload],
    queryFn: async () => {
      const response = await httpService.post('report/custom-report-list', payload);
      return response.data.data;
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};
